import { debugLog, debugWarn } from '../utils/debugLog';
import { getImageCacheMetrics } from './imageCacheService';
import { performanceServiceManager } from './performanceServiceManager';

export interface ImagePerformanceAggregate {
  totalLoads: number;
  hitRate: number;
  missRate: number;
  averageLoadTime: number;
  mobileShare: number;
  desktopShare: number;
  lastUpdated: number;
}

const DISPATCH_INTERVAL_MS = 15000;
let lastDispatchTimestamp = 0;

const toPercentage = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.min(100, value * 100));
};

const ensureFinite = (value: number): number => (Number.isFinite(value) ? value : 0);

export const getImagePerformanceAggregate = (): ImagePerformanceAggregate | null => {
  try {
    const snapshot = getImageCacheMetrics();
    const loads = snapshot.loads;

    if (!loads.total) {
      return null;
    }

    const totalLoads = ensureFinite(loads.total);
    const hits = ensureFinite(loads.hits);
    const misses = ensureFinite(loads.misses);
    const averageLoadTime = ensureFinite(loads.averageLoadTime);
    const mobileLoads = ensureFinite(loads.mobileLoads);
    const desktopLoads = ensureFinite(loads.desktopLoads);

    return {
      totalLoads,
      hitRate: totalLoads ? hits / totalLoads : 0,
      missRate: totalLoads ? misses / totalLoads : 0,
      averageLoadTime,
      mobileShare: totalLoads ? mobileLoads / totalLoads : 0,
      desktopShare: totalLoads ? desktopLoads / totalLoads : 0,
      lastUpdated: snapshot.lastUpdated
    };
  } catch (error) {
    debugWarn('⚠️ Failed to build image performance aggregate', error);
    return null;
  }
};

export const forwardImagePerformanceAggregate = (options: { force?: boolean } = {}): void => {
  const aggregate = getImagePerformanceAggregate();
  if (!aggregate) {
    return;
  }

  const now = Date.now();
  if (!options.force && now - lastDispatchTimestamp < DISPATCH_INTERVAL_MS) {
    return;
  }

  lastDispatchTimestamp = now;

  if (process.env.NODE_ENV === 'development') {
    debugLog('📡 Image performance aggregate', {
      totalLoads: aggregate.totalLoads,
      hitRate: `${toPercentage(aggregate.hitRate).toFixed(2)}%`,
      missRate: `${toPercentage(aggregate.missRate).toFixed(2)}%`,
      averageLoadTime: `${aggregate.averageLoadTime.toFixed(0)}ms`
    });
  }

  performanceServiceManager.addCustomMetric('imagePerformance.hitRate', toPercentage(aggregate.hitRate));
  performanceServiceManager.addCustomMetric('imagePerformance.missRate', toPercentage(aggregate.missRate));
  performanceServiceManager.addCustomMetric('imagePerformance.averageLoadTime', aggregate.averageLoadTime);
  performanceServiceManager.addCustomMetric('imagePerformance.totalLoads', aggregate.totalLoads);
  performanceServiceManager.addCustomMetric('imagePerformance.mobileShare', toPercentage(aggregate.mobileShare));
  performanceServiceManager.addCustomMetric('imagePerformance.desktopShare', toPercentage(aggregate.desktopShare));
};

export const __resetImagePerformanceDispatchState = () => {
  lastDispatchTimestamp = 0;
};
